import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Step3Props } from './types';
import { EditIcon, UploadIcon, EraserIcon, CheckIcon, ScaleIcon } from '../create-contract/icons';

const LEGAL_CLAUSES = [
    { hk: 'CO_LEGAL_TAXES_HEADING',           bk: 'CO_LEGAL_TAXES_BODY' },
    { hk: 'CO_LEGAL_INSURANCE_HEADING',       bk: 'CO_LEGAL_INSURANCE_BODY' },
    { hk: 'CO_LEGAL_CONFIDENTIALITY_HEADING', bk: 'CO_LEGAL_CONFIDENTIALITY_BODY' },
    { hk: 'CO_LEGAL_ROLE_HEADING',            bk: 'CO_LEGAL_ROLE_BODY' },
    { hk: 'CO_LEGAL_LIMITATION_HEADING',      bk: 'CO_LEGAL_LIMITATION_BODY' },
    { hk: 'CO_LEGAL_ACK_HEADING',             bk: 'CO_LEGAL_ACK_BODY' },
] as const;

const Step3LegalSignatures: React.FC<Step3Props> = (props) => {
    const {
        t, errors, setErrors,
        hasAgreed, setHasAgreed,
        signDate,
        signerName, setSignerName,
        hasSignature, setHasSignature,
        canvasRef,
        initialSignatureData,
        customerName,
        isCustomerSigned,
    } = props;

    const [signatureMode, setSignatureMode] = useState<'draw' | 'upload'>('draw');
    const isDrawingRef = useRef(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!initialSignatureData) return;
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;
        const img = new Image();
        img.onload = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            setHasSignature(true);
        };
        img.src = `data:image/png;base64,${initialSignatureData}`;
    }, [initialSignatureData]);

    const clearError = (key: string) =>
        setErrors(prev => { const n = { ...prev }; delete n[key]; return n; });

    // ── Canvas helpers ──────────────────────────────────────────────────────

    // Pointer events cover mouse + touch + pen with one code path, so drawing
    // works on mobile (the canvas has `touch-action: none` so it won't scroll).
    const getCanvasPos = (e: React.PointerEvent) => {
        const canvas = canvasRef.current!;
        const rect   = canvas.getBoundingClientRect();
        return {
            x: (e.clientX - rect.left) * (canvas.width / rect.width),
            y: (e.clientY - rect.top)  * (canvas.height / rect.height),
        };
    };

    const onPointerDown = (e: React.PointerEvent) => {
        if (signatureMode !== 'draw') return;
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;
        e.preventDefault();
        try { canvas.setPointerCapture(e.pointerId); } catch { /* ignore */ }
        isDrawingRef.current = true;
        const { x, y } = getCanvasPos(e);
        ctx.beginPath();
        ctx.moveTo(x, y);
    };

    const onPointerMove = (e: React.PointerEvent) => {
        if (!isDrawingRef.current) return;
        const ctx = canvasRef.current?.getContext('2d');
        if (!ctx) return;
        e.preventDefault();
        const { x, y } = getCanvasPos(e);
        ctx.lineTo(x, y);
        ctx.strokeStyle = '#2A214B';
        ctx.lineWidth   = 2;
        ctx.lineCap     = 'round';
        ctx.lineJoin    = 'round';
        ctx.stroke();
        setHasSignature(true);
        clearError('signature');
    };

    const onPointerEnd = () => { isDrawingRef.current = false; };

    const clearSignature = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
        setHasSignature(false);
    };

    const onUploadSignature = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate type (PNG / JPG / JPEG) + size (max 1 MB) — same as logo upload.
        const fileFormat = file.name.split('.').pop()?.toLowerCase();
        if (!file.type.startsWith('image/') || !(fileFormat === 'png' || fileFormat === 'jpg' || fileFormat === 'jpeg')) {
            toast.error(t('LOGO_FORMAT_NOT_SUPPORTED'));
            e.target.value = '';
            return;
        }
        if (file.size > 1024 * 1024) {
            toast.error(t('FILE_TOO_LARGE_1MB'));
            e.target.value = '';
            return;
        }

        const reader = new FileReader();
        reader.onload = ev => {
            const canvas = canvasRef.current;
            const ctx    = canvas?.getContext('2d');
            if (!canvas || !ctx) return;
            const img  = new Image();
            img.onload = () => {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                // Contain-fit + centre so the signature keeps its aspect ratio
                // instead of being stretched across the whole canvas.
                const cw = canvas.width;
                const ch = canvas.height;
                const scale = Math.min(cw / img.width, ch / img.height) || 1;
                const w = img.width * scale;
                const h = img.height * scale;
                ctx.drawImage(img, (cw - w) / 2, (ch - h) / 2, w, h);
                setHasSignature(true);
                clearError('signature');
            };
            img.src = ev.target?.result as string;
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    // ───────────────────────────────────────────────────────────────────────

    return (
        <>
            {/* Legal Terms */}
            <div className="cc-card">
                <div className="cc-card-header">
                    <span className="cc-card-icon"><ScaleIcon /></span>
                    <div className="cc-card-heading-text">
                        <h3 className="cc-card-title">{t('CO_LEGAL_CARD_TITLE')}</h3>
                    </div>
                </div>
                <div className="cc-card-body">
                    <div className="cc-alert-box cc-legal-alert">
                        {LEGAL_CLAUSES.map(({ hk, bk }) => (
                            <div key={hk} className="cc-legal-item">
                                <p className="cc-legal-heading">{t(hk)}</p>
                                <p className="cc-legal-body">{t(bk)}</p>
                            </div>
                        ))}
                    </div>

                    <div
                        className={`cc-confirm-row${errors.hasAgreed ? ' cc-confirm-row--error' : ''}`}
                        onClick={() => { setHasAgreed(a => !a); clearError('hasAgreed'); }}
                    >
                        <div className={`cc-confirm-box${hasAgreed ? ' checked' : ''}`}>
                            {hasAgreed && <CheckIcon />}
                        </div>
                        <span className="cc-confirm-text">{t('CO_REVIEW_CONFIRM_TEXT')}</span>
                    </div>
                    {errors.hasAgreed && (
                        <span className="cc-field-error" style={{ marginTop: 4 }}>{errors.hasAgreed}</span>
                    )}
                </div>
            </div>

            {/* Signatures */}
            <div className="cc-card cc-card--mt">
                <div className="cc-card-header">
                    <span className="cc-card-icon"><EditIcon /></span>
                    <div className="cc-card-heading-text">
                        <h3 className="cc-card-title">{t('CO_SIG_CARD_TITLE')}</h3>
                    </div>
                </div>
                <div className="cc-card-body">
                    <div className="cc-sig-grid">

                        {/* Freelancer signature panel */}
                        <div className="cc-sig-panel">
                            <p className="cc-label-bold">{t('CO_SIG_FREELANCER_LABEL')}</p>

                            <div className="cc-field" style={{ maxWidth: 360 }}>
                                <label className="cc-label">{t('CO_SIG_DATE_LABEL')}</label>
                                <div className="cc-input-icon-wrap">
                                    <input
                                        className="cc-input"
                                        type="text"
                                        value={signDate}
                                        readOnly
                                        disabled
                                    />
                                </div>
                            </div>

                            <div className="cc-field">
                                <label className="cc-label">{t('CO_SIG_SIGNATURE_LABEL')}</label>
                                <div className="cc-sig-btns">
                                    <button
                                        type="button"
                                        className={`cc-sig-btn cc-sig-btn--draw${signatureMode === 'draw' ? ' active' : ''}`}
                                        onClick={() => { setSignatureMode('draw'); clearSignature(); }}
                                    >
                                        {t('CO_SIG_DRAW_BTN')} <EditIcon />
                                    </button>
                                    <button
                                        type="button"
                                        className="cc-sig-btn cc-sig-btn--upload"
                                        onClick={() => { setSignatureMode('upload'); fileInputRef.current?.click(); }}
                                    >
                                        {t('CO_SIG_UPLOAD_BTN')} <UploadIcon />
                                    </button>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        onChange={onUploadSignature}
                                    />
                                </div>

                                <div className={`cc-canvas-wrap${hasSignature ? ' has-sig' : ''}${errors.signature ? ' cc-canvas-wrap--error' : ''}`}>
                                    <canvas
                                        ref={canvasRef}
                                        width={400}
                                        height={110}
                                        className="cc-canvas"
                                        onPointerDown={onPointerDown}
                                        onPointerMove={onPointerMove}
                                        onPointerUp={onPointerEnd}
                                        onPointerLeave={onPointerEnd}
                                        onPointerCancel={onPointerEnd}
                                    />
                                    {!hasSignature && (
                                        <span className="cc-canvas-hint">
                                            {signatureMode === 'draw' ? t('CO_SIG_CANVAS_HINT_DRAW') : t('CO_SIG_CANVAS_HINT_UPLOAD')}
                                        </span>
                                    )}
                                </div>
                                {errors.signature && <span className="cc-field-error">{errors.signature}</span>}
                                {hasSignature && (
                                    <button type="button" className="cc-btn-clear" onClick={clearSignature}>
                                        <EraserIcon /> {t('CO_SIG_CLEAR_BTN')}
                                    </button>
                                )}
                            </div>

                            <div className="cc-field">
                                <label className="cc-label">{t('CO_SIG_NAME_LABEL')}</label>
                                <input
                                    className={`cc-input${errors.signerName ? ' cc-input--error' : ''}`}
                                    value={signerName}
                                    onChange={e => { setSignerName(e.target.value); clearError('signerName'); }}
                                    placeholder={t('CO_SIG_NAME_HINT')}
                                />
                                {errors.signerName && <span className="cc-field-error">{errors.signerName}</span>}
                            </div>
                        </div>

                        {/* Invozio pre-signed panel */}
                        <div className="cc-sig-panel cc-sig-panel--invozio">
                            <p className="cc-label-bold">{t('CO_SIG_INVOZIO_LABEL')}</p>
                            <div className="cc-invozio-sig-box">
                                <p className="cc-invozio-name">Kevin Zad, CEO</p>
                                <p className="cc-invozio-pre">{t('CO_SIG_INVOZIO_PRE_SIGNED')}</p>
                            </div>
                            <div className="cc-invozio-row">
                                <span className="cc-invozio-label">{t('CO_SIG_PLACE_LABEL')}</span>
                                <span className="cc-invozio-value">Tallinn</span>
                            </div>
                            <div className="cc-invozio-row">
                                <span className="cc-invozio-label">{t('CO_SIG_NAME_LABEL')}</span>
                                <span className="cc-invozio-value">Kevin Zad, CEO</span>
                            </div>
                        </div>

                        <div className="cc-sig-panel cc-sig-panel--customer">
                            <p className="cc-label-bold">{t('CO_SIG_CUSTOMER_LABEL')}</p>
                            <div className="cc-invozio-sig-box">
                                <p className="cc-invozio-name">
                                    {customerName || t('CO_SIG_CUSTOMER_PLACEHOLDER_NAME')}
                                </p>
                                <span className={`cc-customer-sig-badge${isCustomerSigned ? ' is-signed' : ''}`}>
                                    {t(isCustomerSigned ? 'CO_SIG_CUSTOMER_SIGNED_BADGE' : 'CO_SIG_CUSTOMER_PENDING_BADGE')}
                                </span>
                                {!isCustomerSigned && (
                                    <p className="cc-invozio-pre">{t('CO_SIG_CUSTOMER_PENDING_BODY')}</p>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </>
    );
};

export default Step3LegalSignatures;
